
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { checkAndInitGA, initializeGA, disableGA } from '../utils/analytics';

interface CookieBannerProps {
  policyUrl?: string;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ policyUrl = '/privacy-policy' }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already interacted with the cookie banner
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    // If no preference is stored, show the banner after a short delay
    if (!cookieConsent) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else {
      // Initialize or disable GA based on stored preference
      checkAndInitGA();
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    initializeGA(); // Initialize Google Analytics
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    disableGA(); // Disable Google Analytics
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-0 right-0 z-50 px-4 md:px-6 mx-auto max-w-4xl">
      <div className="p-4 md:p-6 bg-dark-card rounded-lg border border-dark-border shadow-lg animate-fade-up flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="flex-shrink-0 p-2 bg-dark-bg/50 rounded-full">
          <Cookie className="w-6 h-6 text-brand" />
        </div>
        
        <div className="flex-grow">
          <h3 className="text-lg font-semibold text-white mb-2">Cookie Notice</h3>
          <p className="text-gray-300 text-sm md:text-base">
            We use cookies to enhance your experience on our website. By continuing to browse, you agree to our use of cookies. 
            See our <Link to={policyUrl} className="text-gray-400 underline hover:text-brand-light transition-colors">Privacy Policy</Link> for more information.
          </p>
        </div>
        
        <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={handleReject}
            className="bg-transparent border-dark-border text-gray-300 hover:text-white hover:border-gray-500"
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
          <Button
            onClick={handleAccept}
            className="bg-brand hover:bg-brand-light text-white"
          >
            <Check className="w-4 h-4 mr-2" />
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
